import {
    ActionReducerMapBuilder,
    createAsyncThunk,
    createSlice,
    Draft,
    SerializedError,
} from '@reduxjs/toolkit';
import {
    urlParamReplicaId,
    urlProjectCreateSupportTicket,
    urlProjectLogSupportTicket,
    urlSupportTemplates,
    urlUserDataForSupportTicketCreation,
} from '../../Constants';
import { IAction, IAppReduxStore, SliceProps } from '../store';
import { GlobalVariables } from '../../GlobalVariables';
import { resetUtilsFileUploadState, uploadFiles } from '../general/sliceFileUpload';
import { resetFileState as resetFileUploadState } from '../import/sliceImportFileUpload';
import { apiFetch } from '../../util/ProjectUtils';

const sortTemplates = (templates: ISupportTemplate[]) =>
    [...templates].sort((a, b) =>
        a.description.split('.')[0].localeCompare(b.description.split('.')[0]),
    );

/**
 * Initial slice state value.
 */
const initialState: IProjectSupportTicketCreationState = {
    isDialogOpen: false,
    isSupportTicketCreated: false,
    templates: null,
    userData: null,
    loading: false,
    error: null,
};

/**
 * Collection of the reducers for this slice.
 */
const reducers = {
    openProjectSupportTicketCreationDialog(
        state: IProjectSupportTicketCreationState,
        action: IAction<boolean>,
    ) {
        state.isDialogOpen = action.payload;
    },
    closeProjectSupportTicketCreationDialog(state: IProjectSupportTicketCreationState) {
        state.isDialogOpen = false;
        state.isSupportTicketCreated = false;
    },
};

/**
 * Collection of the thunks for this slice, along with their reducer functions.
 */
const thunks = {
    actionLogSupportTicket: async (config: { replicaId: string; universalIds: string[]; supportTicketUrl: string }): Promise<string> => {
        const { replicaId, universalIds, supportTicketUrl } = config;
        try {
            const formData = new FormData();
            formData.append('supportTicketUrl', supportTicketUrl);
            formData.append('universalIds', universalIds.join(';'));

            const request: RequestInit = {
                method: 'POST',
                body: formData,
            };

            const url = urlProjectLogSupportTicket.replace(urlParamReplicaId, replicaId);
            const response = await apiFetch(url, request);

            return response.ok
                ? Promise.resolve(await response.text())
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },
    
    getSupportTemplates: async (config: { replicaId: string }) => {
        const { replicaId } = config;
        try {
            const request: RequestInit = {
                headers: {
                    replicaId,
                },
            };

            const response = await apiFetch(urlSupportTemplates, request);
            return response.ok
                ? Promise.resolve(await response.json())
                : Promise.reject(new Error(await response.text()));
        } catch (e: unknown) {
            return Promise.reject(e as Error);
        }
    },

    getUserData: async (config: { replicaId: string }) => {
        const { replicaId } = config;
        try {
            const request: RequestInit = {
                headers: {
                    replicaId,
                },
            };

            const response = await apiFetch(urlUserDataForSupportTicketCreation, request);
            return response.ok
                ? Promise.resolve(await response.json())
                : Promise.reject(new Error(await response.text()));
        } catch (e: unknown) {
            return Promise.reject(e as Error);
        }
    },

    onConfirmCreateSupportTicket: async (
        config: {
            replicaId: string;
            templateUNID: string;
            templateDescription: string;
            subject: string;
            body: string;
            sendCopyTo: 'true' | 'false';
            priority: string;
            copyTo: string[];
            sendTo: string[];
        },
        thunkApi,
    ): Promise<void> => {
        try {
            const {
                replicaId,
                templateUNID,
                templateDescription,
                subject,
                body,
                sendCopyTo,
                priority,
                copyTo,
                sendTo,
            } = config;

            const { data: documents } = (thunkApi.getState() as IAppReduxStore).projectDocuments
                .info;

            const filesArray = GlobalVariables.getAllFiles();

            if (filesArray.length) {
                await thunkApi.dispatch(uploadFiles(filesArray));
            }

            const {
                userData: { replyTo, phoneNumber },
            } = (thunkApi.getState() as IAppReduxStore).projectSupportTicketCreation;

            const { filesPathes } = (thunkApi.getState() as IAppReduxStore).fileUpload;

            const formData = new FormData();

            formData.append(
                'messagedata',
                JSON.stringify({
                    templateDescription,
                    templateUNID,
                    sendTo: sendTo.join(';'),
                    copyTo: copyTo.join(';'),
                    sendCopyTo: sendCopyTo === 'true',
                    replyTo,
                    phoneNumber,
                    subject,
                    body,
                    priority,
                    docList: [...documents.map(item => item.universalId)],
                    fileList: filesPathes ?? [],
                }),
            );

            const request: RequestInit = {
                headers: {
                    replicaId,
                },
                method: 'POST',
                body: formData,
            };

            const response = await apiFetch(urlProjectCreateSupportTicket, request);

            thunkApi.dispatch(resetFileUploadState());
            thunkApi.dispatch(resetUtilsFileUploadState());
            GlobalVariables.resetFiles();

            return response.ok
                ? Promise.resolve(await response.json())
                : Promise.reject(new Error(await response.text()));
        } catch (e: unknown) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Registers the 'extraReducers' functions for the slice's thunks.
     */
    _reducers: (builder: ActionReducerMapBuilder<IProjectSupportTicketCreationState>) => {
        /**
         * Reducers for the 'getSupportTemplates' thunk.
         */
        builder.addCase(
            getSupportTemplates.pending,
            (state: Draft<IProjectSupportTicketCreationState>) => {
                state.loading = true;
                state.error = null;
            },
        );

        builder.addCase(
            getSupportTemplates.fulfilled,
            (
                state: Draft<IProjectSupportTicketCreationState>,
                action: IAction<{ templates: ISupportTemplate[] }>,
            ) => {
                state.templates = sortTemplates(action.payload.templates);
                state.loading = false;
                state.error = null;
            },
        );

        builder.addCase(
            getSupportTemplates.rejected,
            (
                state: Draft<IProjectSupportTicketCreationState>,
                action: IAction<SerializedError>,
            ) => {
                state.loading = false;
                state.error = action.error;
            },
        );

        /**
         * Reducers for the 'getUserData' thunk.
         */
        builder.addCase(getUserData.pending, (state: Draft<IProjectSupportTicketCreationState>) => {
            state.loading = true;
            state.error = null;
        });

        builder.addCase(
            getUserData.fulfilled,
            (
                state: Draft<IProjectSupportTicketCreationState>,
                action: IAction<{ userdata: IUsersDataForSupportTicketCreation }>,
            ) => {
                state.userData = action.payload.userdata;
                state.loading = false;
                state.error = null;
            },
        );

        builder.addCase(
            getUserData.rejected,
            (
                state: Draft<IProjectSupportTicketCreationState>,
                action: IAction<SerializedError>,
            ) => {
                state.loading = false;
                state.error = action.error;
            },
        );

        /**
         * Reducers for the 'onConfirmCreateSupportTicket' thunk.
         */
        builder.addCase(
            onConfirmCreateSupportTicket.pending,
            (state: Draft<IProjectSupportTicketCreationState>) => {
                state.loading = true;
                state.error = null;
            },
        );

        builder.addCase(
            onConfirmCreateSupportTicket.fulfilled,
            (state: Draft<IProjectSupportTicketCreationState>) => {
                state.isSupportTicketCreated = true;

                state.loading = false;
                state.error = null;
            },
        );

        builder.addCase(
            onConfirmCreateSupportTicket.rejected,
            (
                state: Draft<IProjectSupportTicketCreationState>,
                action: IAction<SerializedError>,
            ) => {
                state.loading = false;
                state.error = action.error;
            },
        );
    },
};

export const actionLogSupportTicket = createAsyncThunk(
    urlProjectLogSupportTicket,
    thunks.actionLogSupportTicket,
);

export const getSupportTemplates = createAsyncThunk(
    urlSupportTemplates,
    thunks.getSupportTemplates,
);

export const getUserData = createAsyncThunk(
    urlUserDataForSupportTicketCreation,
    thunks.getUserData,
);

export const onConfirmCreateSupportTicket = createAsyncThunk(
    urlProjectCreateSupportTicket,
    thunks.onConfirmCreateSupportTicket,
);

/**
 * Interface that describes the Support Template.
 */
export interface ISupportTemplate {
    body: string;
    description: string;
    docUNID: string;
    priority: string;
    requiredFields: string[];
    sendTo: string[];
}

/**
 * Interface that describes user's data.
 */
export interface IUsersDataForSupportTicketCreation {
    replyTo: string;
    phoneNumber: string;
}

/**
 * Interface that describes the state object for this redux slice.
 */
export interface IProjectSupportTicketCreationState extends SliceProps {
    templates: ISupportTemplate[];
    isDialogOpen: boolean;
    isSupportTicketCreated: boolean;
    userData: IUsersDataForSupportTicketCreation;
}

/**
 * Create the redux slice.
 */
export const projectSupportTicketCreationSlice = createSlice({
    name: 'projectSupportTicketCreation',
    extraReducers: thunks._reducers,
    initialState,
    reducers,
});

/**
 * Export functions from slice actions.
 */
export const { openProjectSupportTicketCreationDialog, closeProjectSupportTicketCreationDialog } =
    projectSupportTicketCreationSlice.actions;

/**
 * Export slice reducer as default.
 */
export default projectSupportTicketCreationSlice.reducer;
